import { computed, ref, shallowRef, watch, type Ref } from 'vue'
import { exportSimulationPdf } from '../pdf/exportSimulationPdf'
import { calculateIssuanceFees } from '../engine/feeEngine'
import type { FinancialInstrument, IssuanceFees, IssuanceType } from '../types'

interface FeeSimulationPayload {
  amount: number
  financialInstrument: FinancialInstrument
  issuanceType: IssuanceType
}

interface FeeSimulationSessionAdapters {
  exportPdf?: (payload: {
    amount: number
    locale: string
    simulationLabel: string
    simulationTitle: string
  }) => void
  postSimulation?: (payload: FeeSimulationPayload) => Promise<unknown> | unknown
  trackSimulation?: (payload: FeeSimulationPayload) => void
}

export interface UseFeeSimulationSessionArgs {
  adapters?: FeeSimulationSessionAdapters
  html2pdf: () => unknown
  locale: Ref<string>
  t: (key: string) => string
}

export function useFeeSimulationSession(args: UseFeeSimulationSessionArgs) {
  const financialInstrument = shallowRef<FinancialInstrument>()
  const issuanceType = shallowRef<IssuanceType>()
  const amount = shallowRef(0)
  const issuanceFees = ref<IssuanceFees>()
  const isSubmittingSimulation = shallowRef(false)
  const showModal = shallowRef(false)
  const showSimulator = shallowRef(false)
  const submissionError = shallowRef<unknown>(null)

  const simulationTitle = computed(() => {
    return args.t(`simulator.title.${issuanceType.value}.${financialInstrument.value}`)
  })

  function calculate() {
    if (!financialInstrument.value || !issuanceType.value || !amount.value) return

    const payload: FeeSimulationPayload = {
      amount: amount.value,
      financialInstrument: financialInstrument.value,
      issuanceType: issuanceType.value,
    }

    calculateFees(payload)
    submitSimulationSideEffects(payload)
    showSimulator.value = true
  }

  function calculateFees(payload: FeeSimulationPayload): void {
    issuanceFees.value = calculateIssuanceFees(
      payload.financialInstrument,
      payload.issuanceType,
      payload.amount,
    )
  }

  function submitSimulationSideEffects(payload: FeeSimulationPayload): void {
    args.adapters?.trackSimulation?.(payload)

    if (!args.adapters?.postSimulation) {
      return
    }

    submissionError.value = null
    isSubmittingSimulation.value = true

    void Promise.resolve(args.adapters.postSimulation(payload))
      .catch(error => {
        submissionError.value = error
      })
      .finally(() => {
        isSubmittingSimulation.value = false
      })
  }

  function exportPdf() {
    const payload = {
      amount: amount.value,
      locale: args.locale.value,
      simulationLabel: args.t('simulator.simulation2'),
      simulationTitle: simulationTitle.value,
    }

    if (args.adapters?.exportPdf) {
      args.adapters.exportPdf(payload)
    } else {
      exportSimulationPdf({ html2pdf: args.html2pdf, ...payload })
    }

    showModal.value = true
  }

  function closeModal() {
    showModal.value = false
  }

  watch(
    () => [issuanceType.value, financialInstrument.value],
    () => {
      amount.value = 0
    },
  )

  watch(
    () => amount.value,
    () => {
      showSimulator.value = false
    },
  )

  return {
    amount,
    calculate,
    closeModal,
    exportPdf,
    financialInstrument,
    issuanceFees,
    issuanceType,
    isSubmittingSimulation,
    showModal,
    showSimulator,
    simulationTitle,
    submissionError,
  }
}
